// React
import React, {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Mantine
import {
  Stack,
  Alert,
  Stepper,
  Text,
  Group,
  Button,
  MultiSelect,
  Select,
  Accordion,
  Card,
  Badge,
  Checkbox,
  Divider,
  CloseButton,
  SegmentedControl,
} from '@mantine/core';

// Icons
import { IconInfoCircle, IconChevronDown } from '@tabler/icons-react';

// API
import { generateReportV2 } from 'api/action';
import { useListHospitalCodesByProvider } from 'api/resource/claim';
import { useListPayors } from 'api/resource/payor';
import { useGetProvider, useListProvidersBySite } from 'api/resource/provider';
import { useListEobs } from 'api/resource/eob';

// Custom Components
import { DatePickerWithPresets } from 'components/common/DatePickerWithPresets';
import { CustomReportTariffSearch } from './CustomReportTariffSearch';
import { CustomReportDiagnosisSearch } from './CustomReportDiagnosisSearch';
import { hospitalOptions as predefinedHospitalOptions } from '@billing/validation';

// Interfaces
import { ReportType, CustomReportColumnStates } from 'interfaces';

// Hooks
import { useIsProvider } from 'hooks';

// Zustand Stores
import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

// Sentry
import * as Sentry from '@sentry/react';

interface CustomReportModalContentProps {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  close?: () => void;
}

export const CustomReportModalContent: React.FC<
  CustomReportModalContentProps
> = ({ setIsLoading, close }) => {
  const { translate } = useTranslation();
  const { open } = useNotification();
  const { id: siteID } = useCurrentSiteStore();
  const { id: userID, cognitoID, providerID } = useCurrentUserStore();
  const isProvider = useIsProvider();

  // Stepper state
  const [active, setActive] = useState(0);
  const nextStep = () => setActive((curr) => (curr < 4 ? curr + 1 : curr));
  // Restrict step selection: allow selecting any step up to Date Range (index 1).
  // For steps beyond Date Range, require a valid date range first.
  const handleStepClick = (value: number) => {
    if (value <= 1) {
      setActive(value);
      return;
    }
    if (value === 2 && isDateRangeValid) {
      setActive(value);
      return;
    }
    if (isDateRangeValid && isProviderSelected) {
      setActive(value);
    }
  };

  // API Data
  const payorsResult = useListPayors();
  const payorsOptions = useMemo(() => {
    return payorsResult.data
      ? payorsResult.data.map((payor) => ({
          value: payor.id,
          label: payor.name,
        }))
      : [];
  }, [payorsResult.data]);

  const providerListBySiteResult = useListProvidersBySite({ siteID });
  const providerOptions = useMemo(() => {
    const providers = providerListBySiteResult.data ?? [];
    return providers
      .map((provider) => ({
        value: provider.id,
        label: `${provider.firstName} ${provider.lastName}`,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
  }, [providerListBySiteResult.data]);
  const [selectedProviderID, setSelectedProviderID] = useState<
    string | null | undefined
  >(providerID || undefined);
  const isAllProviders = selectedProviderID === null;
  const providerSelectOptions = useMemo(
    () => [
      {
        value: 'all',
        label: translate(
          'pages:reports.customReport.steps.filters.provider.allProviders'
        ),
      },
      ...providerOptions,
    ],
    [providerOptions, translate]
  );
  const providerResult = useGetProvider(selectedProviderID ?? '');

  const providerGroups = useMemo(() => {
    const groups = providerResult.data?.groups ?? [];
    return groups.filter((g) => g.siteID === siteID);
  }, [providerResult.data?.groups, siteID]);
  const providerGroupOptions = useMemo(() => {
    return providerGroups.map((g) => ({
      value: g.id,
      label: g.name,
    }));
  }, [providerGroups]);
  const isProviderGroupLoading = providerResult.isLoading;
  const isProviderGroupError = providerResult.isError;

  const providerHospitalCodesResult = useListHospitalCodesByProvider({
    providerID: selectedProviderID ?? '',
    siteID,
  });
  const allHospitalOptions = useMemo(() => {
    return Array.from(
      new Map(
        predefinedHospitalOptions.map((hospital) => [hospital.value, hospital])
      ).values()
    )
      .map((hospital) => ({
        value: hospital.value,
        label: hospital.label,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
  }, []);
  const hospitalOptions = useMemo(() => {
    if (!selectedProviderID) {
      return allHospitalOptions;
    }
    // If provider-specific lookup fails, gracefully fall back to full list.
    if (providerHospitalCodesResult.isError) {
      return allHospitalOptions;
    }
    const codeSet = new Set(
      (providerHospitalCodesResult.data ?? [])
        .map((code) => code?.trim())
        .filter((code): code is string => Boolean(code))
    );
    if (codeSet.size === 0) {
      return [];
    }
    return allHospitalOptions.filter((hospital) => codeSet.has(hospital.value));
  }, [
    allHospitalOptions,
    providerHospitalCodesResult.data,
    providerHospitalCodesResult.isError,
    selectedProviderID,
  ]);

  const eobResult = useListEobs({});
  const eobOptions = useMemo(() => {
    return (eobResult.data ?? [])
      .map((eob) => ({
        value: eob.id,
        label: `${eob.code} - ${eob.description}`,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
  }, [eobResult.data]);

  // Report Options State
  const dataTypeOptions = [
    {
      value: 'serviceDate',
      label: translate(
        'pages:reports.customReport.steps.type.options.serviceDate.label'
      ),
      description: translate(
        'pages:reports.customReport.steps.type.options.serviceDate.description'
      ),
    },
    {
      value: 'paymentDate',
      label: translate(
        'pages:reports.customReport.steps.type.options.paymentDate.label'
      ),
      description: translate(
        'pages:reports.customReport.steps.type.options.paymentDate.description'
      ),
    },
  ] as const;
  type DataTypeValue = (typeof dataTypeOptions)[number]['value'];
  // Data Type
  const [dataType, setDataType] = useState<DataTypeValue>(
    dataTypeOptions[0].value
  );
  // Date Range
  const { fiscalYearEnd } = useSettingsStore();
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const isDateRangeValid = Boolean(
    dateRange[0] && dateRange[1] && dateRange[0] <= dateRange[1]
  );
  // Payor
  const [payorIDs, setPayorIDs] = useState<string[]>([]);
  // Provider Grouping
  const [groupIDs, setGroupIDs] = useState<string[]>([]);
  // Hospital Codes
  const [hospitalCodes, setHospitalCodes] = useState<string[]>([]);
  // EOB
  const [eobIDs, setEobIDs] = useState<string[]>([]);
  // Tarriff
  const [selectedTariff, setSelectedTariff] = useState<string>('');
  // ICD9 Code
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<string>('');
  // Export format
  type ExportFormats = 'pdf' | 'csv';
  const [exportType, setExportType] = useState<ExportFormats>('pdf');
  const [includePayorSummary, setIncludePayorSummary] = useState(false);
  const [includeProviderSummary, setIncludeProviderSummary] = useState(false);

  type PresetKey = 'default' | 'all' | 'minimal' | 'financial' | 'audit';
  const isProviderSelected = selectedProviderID !== undefined;

  useEffect(() => {
    if (isProvider && providerID && selectedProviderID !== providerID) {
      setSelectedProviderID(providerID);
    }
  }, [isProvider, providerID, selectedProviderID]);

  useEffect(() => {
    setGroupIDs([]);
    setHospitalCodes([]);
    if (!isAllProviders) {
      setIncludeProviderSummary(false);
    }
  }, [isAllProviders, selectedProviderID]);

  // Helper to enforce mandatory columns based on current filters
  const withMandatory = (
    state: CustomReportColumnStates
  ): CustomReportColumnStates => ({
    ...state,
    date: true,
    tariff: Boolean(selectedTariff) || state.tariff,
    diagnosis: Boolean(selectedDiagnosis) || state.diagnosis,
    payor: payorIDs.length > 0 || state.payor,
    groupings: groupIDs.length > 0 || state.groupings,
    eob: eobIDs.length > 0 || state.eob,
    provider: isAllProviders || state.provider,
  });

  // Preset configurations
  const MINIMAL_PRESET: CustomReportColumnStates = {
    date: true,
    claimNumber: true,
    status: true,
    tariff: false,
    diagnosis: false,
    payor: false,
    groupings: false,
    eob: false,
    billedAmount: false,
    paidAmount: false,
    provider: false,
    site: false,
    hospitalName: false,
    patientName: false,
    patientDOB: false,
    patientPHIN: false,
  };
  const AUDIT_PRESET: CustomReportColumnStates = {
    ...MINIMAL_PRESET,
    payor: true,
    groupings: true,
    provider: true,
    site: true,
  };
  const FINANCIAL_PRESET: CustomReportColumnStates = {
    ...MINIMAL_PRESET,
    eob: true,
    billedAmount: true,
    paidAmount: true,
  };
  const DEFAULT_PRESET: CustomReportColumnStates = {
    ...FINANCIAL_PRESET,
    payor: true,
    groupings: true,
    tariff: true,
    diagnosis: false,
    provider: false,
    site: false,
    hospitalName: false,
    patientName: false,
    patientDOB: false,
    patientPHIN: false,
  };
  const ALL_PRESET: CustomReportColumnStates = {
    ...DEFAULT_PRESET,
    diagnosis: true,
    provider: true,
    site: true,
    hospitalName: true,
    patientName: true,
    patientDOB: true,
    patientPHIN: true,
  };

  const PRESET_CONFIG: Record<PresetKey, CustomReportColumnStates> = {
    minimal: MINIMAL_PRESET,
    default: DEFAULT_PRESET,
    financial: FINANCIAL_PRESET,
    audit: AUDIT_PRESET,
    all: ALL_PRESET,
  };

  // Column selection state
  const [columns, setColumns] = useState<CustomReportColumnStates>(
    PRESET_CONFIG.default
  );
  const toggleColumn = (key: keyof typeof columns) =>
    setColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  // Human-friendly labels for columns
  const columnLabels: Record<keyof typeof columns, string> = {
    date:
      dataType === 'serviceDate'
        ? translate(
            'pages:reports.customReport.steps.type.options.serviceDate.label'
          )
        : translate(
            'pages:reports.customReport.steps.type.options.paymentDate.label'
          ),
    eob: translate('pages:reports.columnLabels.eob'),
    claimNumber: translate('pages:reports.columnLabels.claimNumber'),
    billedAmount: translate('pages:reports.columnLabels.billedAmount'),
    paidAmount: translate('pages:reports.columnLabels.paidAmount'),
    provider: translate('pages:reports.columnLabels.provider'),
    site: translate('pages:reports.columnLabels.site'),
    hospitalName: translate('pages:reports.columnLabels.hospitalName'),
    tariff: translate('pages:reports.columnLabels.tariff'),
    diagnosis: translate('pages:reports.columnLabels.diagnosis'),
    status: translate('pages:reports.columnLabels.status'),
    payor: translate('pages:reports.columnLabels.payor'),
    groupings: translate('pages:reports.columnLabels.groupings'),
    patientName: translate('pages:reports.columnLabels.patientName'),
    patientDOB: translate('pages:reports.columnLabels.patientDOB'),
    patientPHIN: translate('pages:reports.columnLabels.patientPHIN'),
  };

  // Determine if a column is enforced by filters (cannot be removed)
  const isColumnMandatory = (key: keyof typeof columns) => {
    switch (key) {
      case 'date':
        return isDateRangeValid;
      case 'tariff':
        return Boolean(selectedTariff);
      case 'diagnosis':
        return Boolean(selectedDiagnosis);
      case 'payor':
        return payorIDs.length > 0;
      case 'groupings':
        return groupIDs.length > 0;
      case 'eob':
        return eobIDs.length > 0;
      case 'provider':
        return isAllProviders;
      default:
        return false;
    }
  };

  const applyPreset = (key: PresetKey) => {
    const preset = withMandatory(PRESET_CONFIG[key]);
    setColumns(preset);
  };

  const handleCreateReport = async () => {
    try {
      setIsLoading(true);
      const selectedColumns = withMandatory(columns);
      const reportPayload = {
        siteID,
        userID,
        cognitoID,
        providerID: selectedProviderID ?? null,
        includePayorSummary,
        includeProviderSummary,
        exportType,
        dataType,
        startDate: dateRange[0],
        endDate: dateRange[1],
        payorIDs,
        groupIDs,
        hospitalCodes,
        eobIDs,
        tariffCode: selectedTariff,
        diagnosisCode: selectedDiagnosis,
        headers: Object.keys(selectedColumns).filter((key) =>
          Boolean(selectedColumns[key])
        ),
      };
      await generateReportV2(ReportType.customReport, reportPayload);
      open?.({
        message: translate('pages:reports.generateReport.inProgress'),
        type: 'success',
        key: 'success',
      });
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        message: translate('pages:reports.generateReport.error'),
        type: 'error',
        key: 'error',
      });
    } finally {
      setIsLoading(false);
      close();
    }
  };

  const onReset = () => {
    setDateRange(['', '']);
    setPayorIDs([]);
    setGroupIDs([]);
    setSelectedProviderID(providerID || undefined);
    setHospitalCodes([]);
    setEobIDs([]);
    setSelectedTariff('');
    setSelectedDiagnosis('');
    setIncludePayorSummary(false);
    setIncludeProviderSummary(false);
    applyPreset('default');
    setActive(0);
  };

  return (
    <Stack gap="sm">
      <Alert
        color="yellow"
        variant="light"
        title={translate('labels.info')}
        icon={<IconInfoCircle />}
      >
        {translate('pages:reports.disclaimer')}
      </Alert>
      <Stepper active={active} onStepClick={handleStepClick}>
        <Stepper.Step
          label={translate('pages:reports.customReport.steps.type.label')}
        >
          <Stack gap="md">
            <Stack gap="0">
              <Text fw={500}>
                {translate('pages:reports.customReport.steps.type.label')}
              </Text>
              <Text size="sm" c="dimmed">
                {translate('pages:reports.customReport.steps.type.description')}
              </Text>
            </Stack>

            <Group gap="md">
              {dataTypeOptions.map((opt) => {
                const selected = dataType === opt.value;
                return (
                  <Card
                    key={opt.value}
                    withBorder
                    shadow={selected ? 'md' : 'xs'}
                    padding="lg"
                    radius="md"
                    style={{
                      cursor: 'pointer',
                      flex: '1 1 45%',
                      minHeight: 150,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                    onClick={() => setDataType(opt.value)}
                  >
                    {selected && (
                      <Badge
                        color="blue"
                        variant="light"
                        size="sm"
                        style={{ position: 'absolute', top: 10, right: 10 }}
                      >
                        {translate('labels.selected')}
                      </Badge>
                    )}
                    <Stack gap={6} style={{ width: '100%' }} align="center">
                      <Text fw={600}>{opt.label}</Text>
                      <Text size="sm" c="dimmed">
                        {opt.description}
                      </Text>
                    </Stack>
                  </Card>
                );
              })}
            </Group>

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={onReset} c="gray">
                {translate('buttons.reset')}
              </Button>
              <Button onClick={nextStep} disabled={!dataType}>
                {translate('buttons.next')}
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step
          label={translate('pages:reports.customReport.steps.dateRange.label')}
        >
          <Stack gap="md">
            <Stack gap="0">
              <Text fw={500}>
                {translate('pages:reports.customReport.steps.dateRange.label')}
              </Text>
              <Text size="sm" c="dimmed">
                {translate(
                  'pages:reports.customReport.steps.dateRange.description'
                )}
              </Text>
            </Stack>

            <DatePickerWithPresets
              dateRange={dateRange}
              setDateRange={setDateRange}
              fiscalYearEndMonth={fiscalYearEnd}
              includeHelpText={false}
            />

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={onReset} c="gray">
                {translate('buttons.reset')}
              </Button>
              <Button onClick={nextStep} disabled={!isDateRangeValid}>
                {translate('buttons.next')}
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step
          label={translate('pages:reports.customReport.steps.filters.label')}
        >
          <Stack gap="md">
            <Stack gap="0">
              <Text fw={500}>
                {translate('pages:reports.customReport.steps.filters.label')}
              </Text>
              <Text size="sm" c="dimmed">
                {translate(
                  'pages:reports.customReport.steps.filters.description'
                )}
              </Text>
            </Stack>

            {!isProvider && (
              <Card withBorder radius="md" p="md">
                <Stack gap="0">
                  <Text fw={500}>
                    {translate(
                      'pages:reports.customReport.steps.filters.provider.label'
                    )}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {translate(
                      'pages:reports.customReport.steps.filters.provider.description'
                    )}
                  </Text>
                </Stack>
                <Select
                  data={providerSelectOptions}
                  value={isAllProviders ? 'all' : selectedProviderID ?? null}
                  onChange={(value) => {
                    if (value === 'all') {
                      setSelectedProviderID(null);
                      return;
                    }
                    setSelectedProviderID(value ?? undefined);
                  }}
                  placeholder={translate(
                    'pages:reports.customReport.steps.filters.provider.placeholder'
                  )}
                  mt="sm"
                  searchable
                  disabled={providerListBySiteResult.isLoading}
                  allowDeselect
                />
              </Card>
            )}

            <Accordion
              variant="separated"
              radius="lg"
              chevronIconSize={18}
              multiple
              defaultValue={[]}
            >
              {/* Payor */}
              <Accordion.Item value="payor">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.payor.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.payor.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    data={payorsOptions}
                    value={payorIDs}
                    onChange={setPayorIDs}
                    placeholder={translate(
                      'pages:reports.customReport.steps.filters.payor.placeholder'
                    )}
                    rightSection={<IconChevronDown size={14} />}
                    rightSectionWidth={30}
                    searchable
                  />
                </Accordion.Panel>
              </Accordion.Item>

              {/* Grouping */}
              <Accordion.Item value="grouping">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.grouping.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.grouping.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    data={providerGroupOptions}
                    value={groupIDs}
                    onChange={setGroupIDs}
                    placeholder={translate(
                      'pages:reports.customReport.steps.filters.grouping.placeholder'
                    )}
                    rightSection={<IconChevronDown size={14} />}
                    rightSectionWidth={30}
                    searchable
                    disabled={
                      isAllProviders ||
                      !isProviderSelected ||
                      isProviderGroupLoading ||
                      providerGroupOptions.length === 0
                    }
                  />
                  {isProviderGroupError && (
                    <Text size="xs" c="red" mt="xs">
                      {translate('errors.failedToLoadProviderGroups')}
                    </Text>
                  )}
                </Accordion.Panel>
              </Accordion.Item>

              {/* Hospital */}
              <Accordion.Item value="hospital">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.hospital.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.hospital.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    data={hospitalOptions}
                    value={hospitalCodes}
                    onChange={setHospitalCodes}
                    placeholder={translate(
                      'pages:reports.customReport.steps.filters.hospital.placeholder'
                    )}
                    rightSection={<IconChevronDown size={14} />}
                    rightSectionWidth={30}
                    searchable
                    disabled={
                      providerHospitalCodesResult.isLoading ||
                      hospitalOptions.length === 0
                    }
                  />
                </Accordion.Panel>
              </Accordion.Item>

              {/* EOB */}
              <Accordion.Item value="eob">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.eob.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.eob.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    data={eobOptions}
                    value={eobIDs}
                    onChange={setEobIDs}
                    placeholder={translate(
                      'pages:reports.customReport.steps.filters.eob.placeholder'
                    )}
                    rightSection={<IconChevronDown size={14} />}
                    rightSectionWidth={30}
                    searchable
                    disabled={eobResult.isLoading || eobOptions.length === 0}
                  />
                </Accordion.Panel>
              </Accordion.Item>

              {/* Tariff */}
              <Accordion.Item value="tariff-diagnosis">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.tariff.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.tariff.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <CustomReportTariffSearch
                    defaultValue=""
                    onChange={(t) => setSelectedTariff(`${t.prefix}${t.code}`)}
                  />
                </Accordion.Panel>
              </Accordion.Item>

              {/* ICD9 Code */}
              <Accordion.Item value="icd9">
                <Accordion.Control>
                  <Stack gap="0">
                    <Text fw={500}>
                      {translate(
                        'pages:reports.customReport.steps.filters.icd9.label'
                      )}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        'pages:reports.customReport.steps.filters.icd9.description'
                      )}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <CustomReportDiagnosisSearch
                    defaultValue=""
                    onChange={(d) => setSelectedDiagnosis(d.icd9)}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={onReset} c="gray">
                {translate('buttons.reset')}
              </Button>
              <Button onClick={nextStep} disabled={!isProviderSelected}>
                {translate('buttons.next')}
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step
          label={translate('pages:reports.customReport.steps.review.label')}
        >
          <Stack gap="0">
            <Text fw={500}>
              {translate('pages:reports.customReport.steps.review.label')}
            </Text>
            <Text size="sm" c="dimmed">
              {translate('pages:reports.customReport.steps.review.description')}
            </Text>
          </Stack>
          {/* Column toggles with sections, badges, and presets */}
          <Card withBorder radius="md" p="md" mt="md">
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text fw={600}>
                  {translate(
                    'pages:reports.customReport.steps.review.columnSelection.label'
                  )}
                </Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => applyPreset('default')}
                  >
                    {translate(
                      'pages:reports.customReport.steps.review.columnSelection.presets.default'
                    )}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => applyPreset('all')}
                  >
                    {translate(
                      'pages:reports.customReport.steps.review.columnSelection.presets.all'
                    )}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => applyPreset('minimal')}
                  >
                    {translate(
                      'pages:reports.customReport.steps.review.columnSelection.presets.minimal'
                    )}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => applyPreset('financial')}
                  >
                    {translate(
                      'pages:reports.customReport.steps.review.columnSelection.presets.financial'
                    )}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => applyPreset('audit')}
                  >
                    {translate(
                      'pages:reports.customReport.steps.review.columnSelection.presets.audit'
                    )}
                  </Button>
                </Group>
              </Group>

              {/* Selected badges (click to remove if not mandatory) */}
              <Group gap="xs" wrap="wrap" mt="xs">
                {Object.entries(withMandatory(columns))
                  .filter(([, val]) => Boolean(val))
                  .map(([key]) => {
                    const k = key as keyof typeof columns;
                    const mandatory = isColumnMandatory(k);
                    return (
                      <Badge
                        key={key}
                        variant="outline"
                        color={mandatory ? 'gray' : 'blue'}
                        rightSection={
                          !mandatory ? (
                            <CloseButton
                              size="xs"
                              onClick={() => toggleColumn(k)}
                            />
                          ) : undefined
                        }
                      >
                        {columnLabels[k]}
                      </Badge>
                    );
                  })}
              </Group>

              <Divider my="sm" />

              {/* Financials */}
              <Text fw={500}>
                {translate(
                  'pages:reports.customReport.steps.review.columnSelection.financialsLabel'
                )}
              </Text>
              <Group gap="md" mt="xs">
                <Checkbox
                  label={translate('pages:reports.columnLabels.eob')}
                  checked={columns.eob}
                  onChange={() => toggleColumn('eob')}
                  disabled={eobIDs.length > 0}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.billedAmount')}
                  checked={columns.billedAmount}
                  onChange={() => toggleColumn('billedAmount')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.paidAmount')}
                  checked={columns.paidAmount}
                  onChange={() => toggleColumn('paidAmount')}
                />
              </Group>

              <Divider my="sm" />

              {/* Claim Details */}
              <Text fw={500}>
                {translate(
                  'pages:reports.customReport.steps.review.columnSelection.claimDetailsLabel'
                )}
              </Text>
              <Group gap="md" mt="xs">
                <Checkbox
                  label={
                    dataType === 'serviceDate'
                      ? translate(
                          'pages:reports.customReport.steps.type.options.serviceDate.label'
                        )
                      : translate(
                          'pages:reports.customReport.steps.type.options.paymentDate.label'
                        )
                  }
                  checked={columns.date}
                  onChange={() => toggleColumn('date')}
                  disabled={isDateRangeValid}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.claimNumber')}
                  checked={columns.claimNumber}
                  onChange={() => toggleColumn('claimNumber')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.status')}
                  checked={columns.status}
                  onChange={() => toggleColumn('status')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.payor')}
                  checked={columns.payor}
                  onChange={() => toggleColumn('payor')}
                  disabled={payorIDs.length > 0}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.groupings')}
                  checked={columns.groupings}
                  onChange={() => toggleColumn('groupings')}
                  disabled={groupIDs.length > 0}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.provider')}
                  checked={isAllProviders || columns.provider}
                  onChange={() => toggleColumn('provider')}
                  disabled={isAllProviders}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.site')}
                  checked={columns.site}
                  onChange={() => toggleColumn('site')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.hospitalName')}
                  checked={columns.hospitalName}
                  onChange={() => toggleColumn('hospitalName')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.tariff')}
                  checked={columns.tariff}
                  onChange={() => toggleColumn('tariff')}
                  disabled={Boolean(selectedTariff)}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.diagnosis')}
                  checked={columns.diagnosis}
                  onChange={() => toggleColumn('diagnosis')}
                  disabled={Boolean(selectedDiagnosis)}
                />
              </Group>

              <Divider my="sm" />

              {/* Patient Identifiers */}
              <Text fw={500}>
                {translate(
                  'pages:reports.customReport.steps.review.columnSelection.patientIdentifiersLabel'
                )}
              </Text>
              <Group gap="md" mt="xs">
                <Checkbox
                  label={translate('pages:reports.columnLabels.patientName')}
                  checked={columns.patientName}
                  onChange={() => toggleColumn('patientName')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.patientDOB')}
                  checked={columns.patientDOB}
                  onChange={() => toggleColumn('patientDOB')}
                />
                <Checkbox
                  label={translate('pages:reports.columnLabels.patientPHIN')}
                  checked={columns.patientPHIN}
                  onChange={() => toggleColumn('patientPHIN')}
                />
              </Group>

              <Divider my="sm" />

              <Text fw={500}>
                {translate(
                  'pages:reports.customReport.steps.review.columnSelection.includeSummaryLabel'
                )}
              </Text>
              <Checkbox
                label={translate(
                  'pages:reports.customReport.steps.review.summary.includePayorSummary'
                )}
                checked={includePayorSummary}
                onChange={(event) =>
                  setIncludePayorSummary(event.currentTarget.checked)
                }
              />
              {isAllProviders && (
                <Checkbox
                  label={translate(
                    'pages:reports.customReport.steps.review.summary.includeProviderSummary'
                  )}
                  checked={includeProviderSummary}
                  onChange={(event) =>
                    setIncludeProviderSummary(event.currentTarget.checked)
                  }
                />
              )}
            </Stack>
          </Card>
          {/* Export format selector */}
          <Card withBorder radius="md" p="md" mt="md">
            <Stack gap="xs">
              <Text fw={600}>
                {translate(
                  'pages:reports.customReport.steps.review.exportFormat.label'
                )}
              </Text>
              <SegmentedControl
                value={exportType}
                onChange={(val) => setExportType(val as ExportFormats)}
                data={[
                  { label: translate('common:fileTypes.pdf'), value: 'pdf' },
                  { label: translate('common:fileTypes.csv'), value: 'csv' },
                ]}
              />
            </Stack>
          </Card>
          {/* Selected options summary */}
          <Card withBorder radius="md" p="md" mt="md">
            <Stack gap="sm">
              <Text fw={600}>
                {translate(
                  'pages:reports.customReport.steps.review.summary.label'
                )}
              </Text>
              <Group wrap="wrap" gap="xs">
                <Badge variant="light" color="blue">
                  {dataType === 'serviceDate'
                    ? translate(
                        'pages:reports.customReport.steps.review.summary.dateTypeServiceDate'
                      )
                    : translate(
                        'pages:reports.customReport.steps.review.summary.dateTypePaymentDate'
                      )}
                </Badge>
                <Badge variant="light" color="cyan">
                  {exportType.toUpperCase()} Export
                </Badge>
                {isAllProviders && (
                  <Badge variant="light" color="grape">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.allProvidersSummary'
                    )}
                  </Badge>
                )}
                {!isAllProviders && selectedProviderID && (
                  <Badge variant="light" color="grape">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.providerSummary',
                      {
                        selectedProvider:
                          providerOptions.find(
                            (o) => o.value === selectedProviderID
                          )?.label || selectedProviderID,
                      }
                    )}
                  </Badge>
                )}
                {isDateRangeValid && (
                  <Badge variant="light" color="blue">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.dateRangeSummary',
                      {
                        startDate: dateRange[0],
                        endDate: dateRange[1],
                      }
                    )}
                  </Badge>
                )}
                {payorIDs.length > 0 && (
                  <Badge variant="light" color="grape">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.payorsSummary',
                      {
                        selectedPayors: payorIDs
                          .map(
                            (id) =>
                              payorsOptions.find((o) => o.value === id)?.label
                          )
                          .filter(Boolean)
                          .join(', '),
                      }
                    )}
                  </Badge>
                )}
                {groupIDs.length > 0 && (
                  <Badge variant="light" color="grape">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.groupsSummary',
                      {
                        selectedGroups: groupIDs
                          .map(
                            (id) =>
                              providerGroupOptions.find((o) => o.value === id)
                                ?.label
                          )
                          .filter(Boolean)
                          .join(', '),
                      }
                    )}
                  </Badge>
                )}
                {hospitalCodes.length > 0 && (
                  <Badge variant="light" color="grape">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.hospitalSummary',
                      {
                        selectedHospitalCodes: hospitalCodes.join(', '),
                      }
                    )}
                  </Badge>
                )}
                {eobIDs.length > 0 && (
                  <Badge variant="light" color="teal">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.eobSummary',
                      {
                        selectedEobs: eobIDs
                          .map(
                            (id) =>
                              eobOptions.find((o) => o.value === id)?.label
                          )
                          .filter(Boolean)
                          .join(', '),
                      }
                    )}
                  </Badge>
                )}
                {selectedTariff && (
                  <Badge variant="light" color="indigo">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.tariffsSummary',
                      {
                        selectedTariffs: selectedTariff,
                      }
                    )}
                  </Badge>
                )}
                {selectedDiagnosis && (
                  <Badge variant="light" color="indigo">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.icd9Summary',
                      {
                        selectedICD9Codes: selectedDiagnosis,
                      }
                    )}
                  </Badge>
                )}
                {includePayorSummary && (
                  <Badge variant="light" color="orange">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.payorSummaryIncluded'
                    )}
                  </Badge>
                )}
                {includeProviderSummary && (
                  <Badge variant="light" color="orange">
                    {translate(
                      'pages:reports.customReport.steps.review.summary.providerSummaryIncluded'
                    )}
                  </Badge>
                )}
              </Group>
            </Stack>
          </Card>
          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={onReset} c="gray">
              {translate('buttons.reset')}
            </Button>
            <Button
              variant="filled"
              onClick={handleCreateReport}
              disabled={!isProviderSelected}
            >
              {translate('buttons.generate')}
            </Button>
          </Group>
        </Stepper.Step>
      </Stepper>
    </Stack>
  );
};
